import { TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ModelSavingData } from "src/hooks/use-model-saving-data";

export type TruxNumberFieldProps<T> = {
  modelSavingData: ModelSavingData<T>,
  field: keyof T,
  allowNegative?: boolean,
  onChange?: (t: T) => boolean | void
}

const ALLOWED_KEYS = new Set<string>();
ALLOWED_KEYS.add("0");
ALLOWED_KEYS.add("1");
ALLOWED_KEYS.add("2");
ALLOWED_KEYS.add("3");
ALLOWED_KEYS.add("4");
ALLOWED_KEYS.add("5");
ALLOWED_KEYS.add("6");
ALLOWED_KEYS.add("7");
ALLOWED_KEYS.add("8");
ALLOWED_KEYS.add("9");
ALLOWED_KEYS.add("Backspace");
ALLOWED_KEYS.add("Delete");
ALLOWED_KEYS.add("ArrowLeft");
ALLOWED_KEYS.add("ArrowRight");
const NEGATIVE_KEY = "-";

export default function TruxNumberField<T>({ modelSavingData, field, onChange, allowNegative = false }: TruxNumberFieldProps<T>) {
  const currentValue = modelSavingData.model?.[field];
  const [localValue, setLocalValue] = useState("0");

  useEffect(() => {
    if (Number(`${localValue}`) !== currentValue) {
      setLocalValue(`${currentValue}`);
    }
  }, [currentValue, setLocalValue, localValue]);

  const fieldOnKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowed = e.metaKey || ALLOWED_KEYS.has(e.key) || (allowNegative && e.key === NEGATIVE_KEY);
    if (!allowed) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [allowNegative]);

  const fieldOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;

    const regex = /^-?\d+$/;
    if (val === "" || regex.test(val)) {
      setLocalValue(val as any);
      // we need to update our existing val to val
      const valNumber = Number(`${val}`);

      const prev = modelSavingData.model?.[field] as any;
      modelSavingData.update(data => {
        data[field] = valNumber as any;
        modelSavingData.model![field] = valNumber as any;

        if (onChange && onChange(data)) {
          data[field] = prev;
        }
      });
    }
  }, [modelSavingData, field, onChange]);

  return <TextField size="small" sx={{ display: "flex", width: "100%" }} value={localValue} onChange={fieldOnChange} onKeyDown={fieldOnKeyDown} />
}